import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

const api = axios.create({
    baseURL: API,
    withCredentials: true,
});

// Track if we've already triggered a redirect to avoid loops
let isRedirecting = false;

// Public routes must NEVER auto-bounce to /login on a stray 401 — anon
// visitors on these pages are expected. Any component that hits an
// authenticated endpoint from here (e.g., an autocomplete widget) should
// just surface an empty state, not a page reload.
const PUBLIC_ROUTE_PREFIXES = [
    "/",
    "/jobs",
    "/apply",
    "/careers",
    "/candidate/signup",
    "/candidate/login",
    "/login",
    "/forgot-password",
    "/reset-password",
    "/resume",
];

const isPublicPath = (path) => {
    if (!path) return false;
    // Exact "/" match without matching every path prefix
    if (path === "/") return true;
    return PUBLIC_ROUTE_PREFIXES.some(
        (p) => p !== "/" && (path === p || path.startsWith(`${p}/`) || path.startsWith(`${p}?`)),
    );
};

api.interceptors.response.use(
    (r) => r,
    (error) => {
        const status = error?.response?.status;
        const url = error?.config?.url || "";
        const detail = error?.response?.data?.detail;
        // V2.0.36 — new-device kick. Backend returns 401 with structured
        // detail {code: "session_ended", reason: "new_device"} when the
        // user's session_id no longer matches the token (i.e. they signed
        // in on another device). Fire a window event so IdleLogout can
        // surface a friendly banner + redirect. This runs BEFORE the
        // generic 401 → /login bounce below.
        if (status === 401 && detail && typeof detail === "object" && detail.code === "session_ended") {
            if (typeof window !== "undefined") {
                window.dispatchEvent(new CustomEvent("naukri:session-ended", { detail }));
            }
            return Promise.reject(error);
        }
        // On 401 anywhere except /auth/me (which is expected to 401 when anon)
        // bounce the user to /login and clear local auth.
        if (status === 401 && !url.includes("/auth/me") && !url.includes("/auth/login")) {
            if (!isRedirecting && typeof window !== "undefined") {
                const path = window.location.pathname || "";
                // Whitelist public routes so anon visitors are never yanked
                // off a public page by a stray authenticated request.
                if (isPublicPath(path)) {
                    return Promise.reject(error);
                }
                isRedirecting = true;
                // small delay to allow any in-flight UI updates to flush
                setTimeout(() => {
                    isRedirecting = false;
                    // Route-aware bounce: candidate area → candidate login,
                    // everything else → recruiter login. Keeps the two flows
                    // visually and functionally distinct.
                    const target = path.startsWith("/candidate") ? "/candidate/login" : "/login";
                    if (path !== target) {
                        window.location.replace(target);
                    }
                }, 50);
            }
        }
        // On 402 (plan limit reached), fire a global toast with an upgrade CTA
        // — individual callers may still catch/handle if they need custom UI.
        if (status === 402 && typeof window !== "undefined") {
            const detail = error?.response?.data?.detail;
            if (detail && detail.code === "plan_limit") {
                window.dispatchEvent(new CustomEvent("naukri:plan-limit", { detail }));
            }
        }
        return Promise.reject(error);
    },
);

export default api;

export function formatApiError(detail) {
    if (detail == null) return null;
    if (typeof detail === "string") return detail;
    if (Array.isArray(detail))
        return detail
            .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
            .filter(Boolean)
            .join(" ");
    if (detail && typeof detail.msg === "string") return detail.msg;
    if (detail && typeof detail.message === "string") return detail.message;
    // V1.5.8.18 — was `String(detail)` which returned "[object Object]" for
    // any unexpected shape. JSON.stringify at least surfaces the actual
    // keys so we can debug from the UI error banner.
    try {
        return JSON.stringify(detail);
    } catch {
        return "Unknown error";
    }
}

// Build a user-facing error string from an axios error. Distinguishes
// network/CORS failures (no response) from real server-side errors so
// users don't always see a generic "Something went wrong" message.
export function describeAxiosError(err) {
    if (!err) return "Something went wrong. Please try again.";
    const detail = err.response?.data?.detail;
    // Structured plan-limit responses (HTTP 402)
    if (detail && typeof detail === "object" && detail.code === "plan_limit") {
        return detail.message || "Plan limit reached. Please upgrade.";
    }
    const fromDetail = formatApiError(detail);
    if (fromDetail) return fromDetail;
    if (err.response) {
        return `Server error ${err.response.status}. Please try again.`;
    }
    // No response at all — network, CORS, blocked cookies, offline, etc.
    if (err.code === "ERR_NETWORK" || /network/i.test(err.message || "")) {
        return "Can't reach the server. Check your connection or disable any ad-blocker / privacy extension.";
    }
    return err.message || "Something went wrong. Please try again.";
}

/**
 * Returns { code: 'plan_limit', ... } if the error is a 402 plan-limit response,
 * else null. Consumers can render a rich upgrade CTA instead of a plain toast.
 */
export function isPlanLimitError(err) {
    const detail = err?.response?.data?.detail;
    if (detail && typeof detail === "object" && detail.code === "plan_limit") {
        return detail;
    }
    return null;
}
